/**
 * PlayerBottomBar
 * ---------------
 * The teacher's control surface, replacing the floating left stack that used to
 * cover the quiz card.
 *
 * One tap reaches playback transport, marker tools and class controls. Roster and
 * approvals are lists, so they live in a sheet — the badge count stays here.
 * On compact widths the secondary items collapse behind an overflow button.
 */

import { useEffect, useRef, useState, type RefObject } from 'react';
import { Play, Pause, RotateCcw, ChevronLeft, ChevronRight, ChevronUp, Radio, Zap, Users, Target, MoreHorizontal, Eye, EyeOff, SkipForward, MapPin, ListOrdered, Check, Box, PenTool, Loader2, Images, Minus, Plus, Layers } from 'lucide-react';
import { MarkerToolbar } from './MarkerToolbar';
import { ModelToolbar, type ClipAxis, type ToolbarAsset } from './ModelToolbar';
import { TopicPicker, type TopicPickerItem } from './TopicPicker';
import { LessonImageGrid } from './LessonImagePanel';
import type { ActiveLessonImage } from '../../lib/lesson/buildActiveLesson';

const PHASES: Array<{ phase: string; label: string }> = [
  { phase: 'intro', label: 'Intro' },
  { phase: 'explanation', label: 'Learn' },
  { phase: 'outro', label: 'Summary' },
  { phase: 'quiz', label: 'Quiz' },
];

type Menu = 'phases' | 'tools' | 'model' | 'images' | 'topics' | 'more' | 'myView' | null;

/**
 * A viewer's own view of the 3D models, for the student bar. Everything here is
 * local to this viewer: nothing reaches the class, and the teacher's own model
 * tools still override it (see lib/classroom/modelPrecedence).
 */
export interface StudentModelControls {
  assets: ToolbarAsset[];
  /** The model these controls act on. */
  selectedKey: string;
  onSelect: (key: string) => void;
  /** 0..1, how far apart the selected model is. */
  explode: number;
  /** False for a single-piece model, which has nothing to take apart. */
  canExplode: boolean;
  onExplodeChange: (t: number) => void;
  /** Multiplier on the size the lesson gave the model; 1 is as placed. */
  scale: number;
  canShrink: boolean;
  canGrow: boolean;
  onScaleStep: (direction: 1 | -1) => void;
  /** Every model back to the lesson's (or, in a class, the teacher's) view. */
  onReset: () => void;
}

/** Close an open popover on a press outside the bar or on Escape. */
function useDismiss(ref: RefObject<HTMLElement>, open: boolean, close: () => void) {
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) close();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Marked as used, so the lesson image card (which also listens for
        // Escape) does not take the class's image down with the popover.
        event.preventDefault();
        close();
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [ref, open, close]);
}

interface PlayerBottomBarProps {
  isHost: boolean;
  compact?: boolean;

  // Playback (student sees a read-only version; host drives)
  playbackState?: 'idle' | 'playing' | 'paused';
  currentPhase?: string | null;
  onPlaybackCommand?: (cmd: 'play' | 'pause' | 'replay', phase?: string) => void;
  /** Student-side local narration control, used when not under teacher control. */
  onLocalPlayToggle?: () => void;
  isPlayingAudio?: boolean;
  playbackLocked?: boolean;

  // Class controls (host)
  controlStudentsEnabled?: boolean;
  onToggleControl?: (enabled: boolean) => void;
  /** True once the teacher has pressed Start class (teacher_playback is no longer idle). */
  classStarted?: boolean;
  /** Whether STUDENTS currently see the in-headset panel. The teacher always keeps theirs. */
  studentUiVisible?: boolean;
  onToggleStudentUi?: (visible: boolean) => void;
  onForceStudentsIn?: () => void;
  canForce?: boolean;
  onDirectView?: () => void;
  liveCount?: number;
  onOpenRoster?: () => void;
  raisedHands?: number;

  // Marker (host)
  markerActive?: boolean;
  markerColor?: string;
  onToggleMarker?: () => void;
  onMarkerColorChange?: (c: string) => void;

  // 3D model (host). Absent/0 partCount hides the toolbar entirely.
  /**
   * The next topic in the chapter, when there is one. Present only for a host.
   * Advancing keeps the class in immersive mode — nobody re-enters VR between
   * topics.
   */
  nextTopicName?: string | null;
  /**
   * True when the chapter has more than one topic. Distinguishes "there is no
   * next topic because this is the last one" from "there is no sequence here at
   * all" — the first deserves a disabled control, the second nothing.
   */
  chapterHasSequence?: boolean;
  /**
   * Phases this lesson actually has content for.
   *
   * The dock offered all four unconditionally, so a teacher could send the
   * class to a Summary that was never written and land them on an empty
   * screen. Undefined keeps every phase, so callers that do not know stay as
   * they were.
   */
  availablePhases?: string[];
  /** Stop navigation for a multi-stop tour, with the destination named. */
  tourStops?: {
    index: number;
    total: number;
    currentLabel?: string;
    nextLabel?: string;
    prevLabel?: string;
    onPrev?: () => void;
    onNext?: () => void;
  } | null;
  /**
   * How many students have finished the quiz, out of how many are in the class.
   * `open: false` means the player is holding Next until they all have: the
   * button turns amber and reads "Waiting", but stays pressable — the player
   * decides what a press does then (V3 asks the teacher to confirm). Absent
   * `open` keeps the plain count, for players with no gate.
   */
  quizProgress?: { done: number; total: number; open?: boolean };
  onAdvanceTopic?: () => void;
  /** The chapter's topics, for jumping the class to any of them. Host only. */
  topics?: TopicPickerItem[];
  onPickTopic?: (topic: TopicPickerItem) => void;
  /** A topic launch is in flight. */
  topicLaunchPending?: boolean;
  modelPartCount?: number;
  /** The scene's 3D assets, so the teacher can pick which one to manipulate. */
  modelAssets?: ToolbarAsset[];
  modelSelectedAssetKey?: string | null;
  onModelSelectAsset?: (key: string | null) => void;
  modelExplode?: number;
  onModelExplodeChange?: (t: number) => void;
  modelIsolated?: boolean;
  modelSelectedPartName?: string | null;
  onToggleModelIsolate?: () => void;
  modelClip?: { axis: ClipAxis; offset: number } | null;
  onModelClipChange?: (clip: { axis: ClipAxis; offset: number } | null) => void;
  onModelReset?: () => void;
  /**
   * Part highlight (host): while on, tapping a part makes it glow with its name
   * for the class. Absent `onToggleModelHighlight` hides the control — the
   * krpano player passes none.
   */
  modelHighlightMode?: boolean;
  onToggleModelHighlight?: () => void;
  /** Parts glowing now; drives "Clear (n)" and the dot on the 3D model button. */
  modelHighlightCount?: number;
  onClearModelHighlights?: () => void;

  /** The lesson's images, for the Images picker. Empty or absent hides it. */
  lessonImages?: ActiveLessonImage[];
  /** Id of the image being shown, or null when none is. */
  activeImageId?: string | null;
  /**
   * Show an image (null takes it down). Pass it only to whoever drives the
   * images: the class host, or a viewer on their own with nobody to follow.
   * A student in a class is handed no picker, and without this there is none.
   */
  onSelectImage?: (imageId: string | null) => void;
  /**
   * The student bar's My view: explode and resize this viewer's own copy of a
   * model. Absent shows no My view button (the krpano player passes none).
   */
  studentModel?: StudentModelControls;
}

export const PlayerBottomBar = (props: PlayerBottomBarProps) => {
  const {
    isHost, compact = false,
    playbackState = 'idle', currentPhase, onPlaybackCommand,
    onLocalPlayToggle, isPlayingAudio = false, playbackLocked = false,
    controlStudentsEnabled = false, onToggleControl, onForceStudentsIn, canForce = false,
    onDirectView, liveCount = 0, onOpenRoster, raisedHands = 0,
    classStarted = false, studentUiVisible = true, onToggleStudentUi,
    markerActive = false, markerColor = '#ffdd33',
    onToggleMarker, onMarkerColorChange,
    nextTopicName = null, chapterHasSequence = false, quizProgress, onAdvanceTopic,
    topics = [], onPickTopic, topicLaunchPending = false,
    availablePhases, tourStops = null,
    modelPartCount = 0, modelExplode = 0, onModelExplodeChange,
    modelAssets = [], modelSelectedAssetKey = null, onModelSelectAsset,
    modelIsolated = false, modelSelectedPartName = null, onToggleModelIsolate,
    modelClip = null, onModelClipChange, onModelReset,
    modelHighlightMode = false, onToggleModelHighlight, modelHighlightCount = 0, onClearModelHighlights,
    lessonImages = [], activeImageId = null, onSelectImage,
    studentModel,
  } = props;

  const [menu, setMenu] = useState<Menu>(null);
  const barRef = useRef<HTMLDivElement>(null);
  useDismiss(barRef, menu !== null, () => setMenu(null));

  const btn = 'inline-flex h-10 shrink-0 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-semibold transition';
  const quiet = 'border-white/12 bg-white/[0.06] text-white/75 hover:bg-white/10';
  const popover = 'absolute bottom-[calc(100%+0.5rem)] z-20 max-w-[calc(100%-1rem)] rounded-xl border border-white/12 bg-zinc-950/95 p-2 shadow-2xl backdrop-blur-2xl';
  const menuButton = (isOpen: boolean) =>
    `${btn} ${isOpen ? 'border-white/30 bg-white/15 text-white' : quiet}`;
  const sectionLabel = 'px-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-white/40';

  const toggle = (name: Menu) => setMenu((current) => (current === name ? null : name));

  /*
    Lesson images: a named grid that opens from the bar.

    The picker used to be a thumbnail strip in the middle of the stage, sitting
    over the models for as long as the lesson had images. Here it takes no room
    until asked for, and the count says there is something to show. Built
    before the student branch because a viewer with nobody to follow (see
    `onSelectImage`) gets the student bar and still picks their own images.
  */
  const showImages = lessonImages.length > 0 && !!onSelectImage;
  const imageGrid = (
    <LessonImageGrid
      images={lessonImages}
      activeImageId={activeImageId}
      onSelect={(imageId) => {
        setMenu(null);
        onSelectImage?.(imageId);
      }}
    />
  );
  const imagesButton = (
    <button
      type="button"
      onClick={() => toggle('images')}
      aria-expanded={menu === 'images'}
      aria-label={`Lesson images (${lessonImages.length})`}
      title={isHost ? 'Show a lesson image to the class' : 'Show a lesson image'}
      className={menuButton(menu === 'images')}
    >
      <Images className="h-3.5 w-3.5" />
      <span className="hidden xl:inline">Images</span>
      <span className="tabular-nums text-white/55">{lessonImages.length}</span>
      {activeImageId && <span className="h-1.5 w-1.5 rounded-full bg-cyan-300" />}
    </button>
  );
  const imagesPopover = menu === 'images' && showImages && (
    <div className={`${popover} left-1/2 -translate-x-1/2`}>
      <div className={sectionLabel}>Lesson images</div>
      {imageGrid}
    </div>
  );

  // ---- Student bar: narration only, and only when the teacher isn't driving ----
  if (!isHost) {
    return (
      <div
        ref={barRef}
        className="pointer-events-auto relative flex h-full w-full items-center justify-center gap-2 border-t border-white/10 bg-zinc-950/80 px-3 backdrop-blur-2xl"
      >
        <button
          type="button"
          onClick={onLocalPlayToggle}
          disabled={playbackLocked}
          title={playbackLocked ? 'Your teacher controls playback' : isPlayingAudio ? 'Pause' : 'Play'}
          className={`inline-flex h-10 items-center gap-2 rounded-xl border px-4 text-sm font-semibold transition ${
            playbackLocked
              ? 'cursor-not-allowed border-white/10 bg-white/[0.03] text-white/35'
              : 'border-primary/50 bg-primary/20 text-primary hover:bg-primary/30'
          }`}
        >
          {isPlayingAudio ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          {playbackLocked ? 'Teacher is leading' : isPlayingAudio ? 'Pause' : 'Play'}
        </button>
        {showImages && imagesButton}
        {studentModel && (
          <button
            type="button"
            onClick={() => toggle('myView')}
            aria-expanded={menu === 'myView'}
            title="Take the 3D model apart or resize it — only you see this"
            className={menuButton(menu === 'myView')}
          >
            <Box className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">My view</span>
            {(studentModel.explode > 0 || studentModel.scale !== 1) && (
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-300" />
            )}
          </button>
        )}
        {imagesPopover}
        {/* The dock twin of the gestures (pinch, wheel, grip and stick), for
            anyone who has not found them — and the one way back from them. */}
        {menu === 'myView' && studentModel && (
          <div className={`${popover} left-1/2 w-[17rem] -translate-x-1/2`}>
            <div className={sectionLabel}>My view</div>
            <div className="flex flex-col gap-2 p-1">
              {studentModel.assets.length > 1 && (
                <select
                  value={studentModel.selectedKey}
                  onChange={(e) => studentModel.onSelect(e.target.value)}
                  aria-label="Model to change"
                  className="h-9 w-full truncate rounded-lg border border-white/12 bg-white/[0.06] px-2 text-xs font-semibold text-white/75 outline-none transition hover:bg-white/10 focus:border-cyan-300/50"
                >
                  {studentModel.assets.map((asset) => (
                    <option key={asset.key} value={asset.key}>
                      {asset.name}
                    </option>
                  ))}
                </select>
              )}
              <label
                className="flex items-center gap-2 text-xs font-semibold text-white/75"
                title={studentModel.canExplode ? 'Pull the model apart' : 'This model is a single piece — nothing to take apart'}
              >
                <Layers className="h-3.5 w-3.5 shrink-0" />
                <span className="w-14">Explode</span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={Math.round(studentModel.explode * 100)}
                  disabled={!studentModel.canExplode}
                  onChange={(e) => studentModel.onExplodeChange(Number(e.target.value) / 100)}
                  aria-label="Explode amount"
                  className="h-1.5 min-w-0 flex-1 cursor-pointer accent-cyan-400 disabled:cursor-not-allowed disabled:opacity-40"
                />
              </label>
              <div className="flex items-center gap-2 text-xs font-semibold text-white/75">
                <Box className="h-3.5 w-3.5 shrink-0" />
                <span className="w-14">Size</span>
                <button
                  type="button"
                  onClick={() => studentModel.onScaleStep(-1)}
                  disabled={!studentModel.canShrink}
                  aria-label="Smaller"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/12 bg-white/[0.06] text-white/75 transition hover:bg-white/10 disabled:opacity-40"
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="w-10 text-center tabular-nums text-white/60">{Math.round(studentModel.scale * 100)}%</span>
                <button
                  type="button"
                  onClick={() => studentModel.onScaleStep(1)}
                  disabled={!studentModel.canGrow}
                  aria-label="Bigger"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/12 bg-white/[0.06] text-white/75 transition hover:bg-white/10 disabled:opacity-40"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
              <button
                type="button"
                onClick={() => {
                  setMenu(null);
                  studentModel.onReset();
                }}
                className={`${btn} ${quiet} justify-center`}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Reset my view
              </button>
              <p className="px-1 text-[10px] leading-snug text-white/40">
                Only you see these changes. Pinch or scroll on a model to resize it; Shift+drag turns it.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---- Flow-aware layout -------------------------------------------------
  //
  // The bar used to render all eleven host controls at equal weight, in a fixed order,
  // whether or not they meant anything yet. A teacher had to hunt for the one button that
  // mattered at that moment, and controls that do nothing (Student UI before the class has
  // started, Direct view before anyone is in) sat next to ones that do.
  //
  // Instead the bar follows the lesson: ONE primary action for the current stage, its
  // likely follow-ups beside it, and everything else demoted to the overflow — still one
  // tap away, never removed.
  const playing = playbackState === 'playing';
  const phase = currentPhase || 'intro';
  const stage: 'gathering' | 'teaching' | 'quiz' | 'review' = !classStarted
    ? 'gathering'
    : phase === 'completed'
      ? 'review'
      : phase === 'quiz'
        ? 'quiz'
        : 'teaching';

  // Only phases the lesson has content for. Advancing walks this list, so Next
  // skips straight over a missing Summary rather than stopping on a blank one.
  const phases = availablePhases ? PHASES.filter((p) => availablePhases.includes(p.phase)) : PHASES;
  const phaseIndex = phases.findIndex((p) => p.phase === phase);
  const goNextPhase = () => {
    if (phases.length === 0) return;
    onPlaybackCommand?.('play', phases[Math.min(Math.max(phaseIndex, 0) + 1, phases.length - 1)].phase);
  };

  /**
   * The single most likely next press, given where the lesson is.
   * Nobody has arrived yet -> bring them in. Everyone is here but nothing is running ->
   * start. Running -> pause. It carries a ring while it is the recommended step so the eye
   * lands on it without reading the bar.
   */
  const cta = (() => {
    if (stage === 'gathering' && liveCount === 0 && canForce) {
      return {
        key: 'force',
        node: (
          <button
            type="button"
            onClick={onForceStudentsIn}
            title="Pull every joined student into this lesson"
            className="inline-flex h-11 shrink-0 items-center gap-2 rounded-xl border border-cyan-300/60 bg-cyan-400/25 px-4 text-sm font-bold text-cyan-50 shadow-lg ring-2 ring-cyan-300/30 transition hover:bg-cyan-400/35"
          >
            <Zap className="h-4 w-4" />
            <span>Bring everyone in</span>
          </button>
        ),
      };
    }
    if (stage === 'review') {
      // The lesson has finished. Offering "Start class" here would restart a completed
      // lesson; what the teacher actually wants next is how the class did.
      return {
        key: 'roster',
        node: (
          <button
            type="button"
            onClick={onOpenRoster}
            title="See how the class did"
            className="relative inline-flex h-11 shrink-0 items-center gap-2 rounded-xl border border-cyan-300/60 bg-cyan-400/25 px-4 text-sm font-bold text-cyan-50 shadow-lg ring-2 ring-cyan-300/30 transition hover:bg-cyan-400/35"
          >
            <Users className="h-4 w-4" />
            <span>See results</span>
            <span className="tabular-nums opacity-80">{liveCount}</span>
          </button>
        ),
      };
    }
    // Taking control and starting the class are ONE intent, so they are one
    // button. setSessionControl deliberately holds the class at idle, which
    // meant the teacher pressed Control and then Play for a single decision,
    // with the two sitting side by side as if they were alternatives.
    if (!controlStudentsEnabled) {
      return {
        key: 'control',
        node: (
          <button
            type="button"
            onClick={() => onToggleControl?.(true)}
            title="Take control — students are held until you start the class"
            className="inline-flex h-11 shrink-0 items-center gap-2 rounded-xl border border-violet-300/60 bg-violet-500/30 px-4 text-sm font-bold text-violet-50 shadow-lg ring-2 ring-violet-300/25 transition hover:bg-violet-500/40"
          >
            <Radio className="h-4 w-4" />
            <span>Take control</span>
          </button>
        ),
      };
    }

    const label = playing ? 'Pause' : playbackState === 'paused' ? 'Resume' : 'Start class';
    const recommend = !classStarted;
    return {
      key: 'play',
      node: (
        <button
          type="button"
          onClick={() => onPlaybackCommand?.(playing ? 'pause' : 'play')}
          aria-label={label}
          title={playing ? 'Pause the class' : playbackState === 'paused' ? 'Resume the class' : 'Start the class for everyone'}
          className={`inline-flex h-11 shrink-0 items-center gap-2 rounded-xl border px-4 text-sm font-bold shadow-lg transition ${
            playing
              ? 'border-amber-300/50 bg-amber-400/20 text-amber-100 hover:bg-amber-400/30'
              : 'border-emerald-300/60 bg-emerald-400/30 text-emerald-50 hover:bg-emerald-400/40'
          } ${recommend ? 'ring-2 ring-emerald-300/30' : ''}`}
        >
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          {/* Mid-class below laptop width the Pause/Resume icon says it; the room goes to Direct view. */}
          <span className={classStarted ? 'hidden lg:inline' : ''}>{label}</span>
        </button>
      ),
    };
  })();

  // Every control, addressable by key so each stage can compose its own bar.
  const controls: Record<string, JSX.Element> = {
    /*
      Stop-to-stop navigation for a tour.

      This also exists as two 32px chevrons in the top bar, which are hidden
      below the `sm` breakpoint and name neither the current stop nor the next
      one — so on a tablet a teacher had no way to move the class at all, and on
      a laptop they were moving it somewhere they could not see. Here it sits
      with the other teaching controls, says where the class is, and says where
      Next goes.
    */
    scenes: (
      <div key="scenes" className="flex shrink-0 items-center gap-0.5 rounded-lg border border-white/12 bg-white/[0.04] p-0.5">
        <button
          type="button"
          onClick={tourStops?.onPrev}
          disabled={!tourStops?.onPrev}
          title={tourStops?.prevLabel ? `Take the class back to ${tourStops.prevLabel}` : 'Previous stop'}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-white/70 transition hover:bg-white/10 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="flex items-center gap-1 px-1.5 text-[11px] font-semibold text-white/75">
          <MapPin className="h-3 w-3 shrink-0 text-white/50" />
          <span className="tabular-nums">
            {(tourStops?.index ?? 0) + 1}/{tourStops?.total ?? 1}
          </span>
          {!compact && tourStops?.currentLabel && (
            <span className="max-w-[10rem] truncate font-normal text-white/55">{tourStops.currentLabel}</span>
          )}
        </span>
        <button
          type="button"
          onClick={tourStops?.onNext}
          disabled={!tourStops?.onNext}
          title={tourStops?.nextLabel ? `Move the class on to ${tourStops.nextLabel}` : 'Next stop'}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-white/70 transition hover:bg-white/10 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    ),
    phases: (
      <div key="phases" className="flex shrink-0 items-center gap-0.5 rounded-lg border border-white/12 bg-white/[0.04] p-0.5">
        {phases.map((p) => (
          <button
            key={p.phase}
            type="button"
            onClick={() => onPlaybackCommand?.('play', p.phase)}
            title={`Send the class to ${p.label}`}
            className={`h-8 rounded-md px-2 text-[11px] font-semibold transition ${
              phase === p.phase ? 'bg-white/15 text-white' : 'text-white/60 hover:text-white'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
    ),
    next: (
      <button
        key="next"
        type="button"
        onClick={goNextPhase}
        aria-label="Next phase"
        title="Advance the class to the next phase"
        className={`${btn} border-teal-300/40 bg-teal-400/15 text-teal-100 hover:bg-teal-400/25`}
      >
        <ChevronRight className="h-4 w-4" />
        {!compact && <span className="hidden lg:inline">Next</span>}
      </button>
    ),
    replay: (
      <button
        key="replay"
        type="button"
        onClick={() => onPlaybackCommand?.('replay')}
        title="Replay the current phase for everyone"
        className={`${btn} ${quiet} text-violet-200`}
      >
        <RotateCcw className="h-4 w-4" />
        {!compact && <span className="hidden xl:inline">Replay</span>}
      </button>
    ),
    // Taking control is the primary button above; this is only ever the release,
    // and it names the consequence rather than the mechanism.
    release: (
      <button
        key="release"
        type="button"
        onClick={() => onToggleControl?.(false)}
        title="Release the class — students move at their own pace again"
        className={`${btn} border-rose-400/45 bg-rose-500/20 text-rose-100 hover:bg-rose-500/30`}
      >
        <Radio className="h-3.5 w-3.5 shrink-0 animate-pulse" />
        {!compact && <span>Let students explore</span>}
      </button>
    ),
    // Paired with the marker: after drawing on something, the next press is almost always
    // "now everyone look at it", so these two sit together rather than at opposite ends.
    direct: (
      <button
        key="direct"
        type="button"
        onClick={onDirectView}
        aria-label="Direct view"
        title="Point the whole class at what you are looking at"
        className={`${btn} ${markerActive ? 'border-amber-300/40 bg-amber-400/15 text-amber-100' : quiet}`}
      >
        <Target className="h-3.5 w-3.5" />
        {!compact && <span className="hidden lg:inline">Direct view</span>}
      </button>
    ),
    studentUi: (
      <button
        key="studentUi"
        type="button"
        onClick={() => onToggleStudentUi?.(!studentUiVisible)}
        disabled={!classStarted}
        aria-pressed={studentUiVisible}
        title={
          !classStarted
            ? 'Start the class first — students explore with a clean view until then'
            : studentUiVisible
              ? 'Students can see the lesson panel — hide it so they explore the scene'
              : 'Show the lesson panel to students'
        }
        className={`${btn} disabled:opacity-40 ${
          studentUiVisible ? 'border-emerald-300/45 bg-emerald-400/20 text-emerald-50' : quiet
        }`}
      >
        {studentUiVisible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
        {!compact && <span>{studentUiVisible ? 'Student UI' : 'Student UI: off'}</span>}
      </button>
    ),
    force: (
      <button
        key="force"
        type="button"
        onClick={onForceStudentsIn}
        disabled={!canForce}
        title="Pull every joined student into this lesson"
        className={`${btn} border-cyan-300/40 bg-cyan-400/15 text-cyan-50 hover:bg-cyan-400/25 disabled:opacity-40`}
      >
        <Zap className="h-3.5 w-3.5" />
        {!compact && <span>Bring everyone in</span>}
      </button>
    ),
    roster: (
      <button
        key="roster"
        type="button"
        onClick={onOpenRoster}
        title="Student roster"
        className={`relative ${btn} ${quiet} ${stage === 'quiz' || stage === 'review' ? 'border-cyan-300/40 text-cyan-50' : ''}`}
      >
        <Users className="h-3.5 w-3.5" />
        <span className="tabular-nums">{liveCount}</span>
        {raisedHands > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-400 px-1 text-[10px] font-bold text-black">
            {raisedHands}
          </span>
        )}
      </button>
    ),
  };

  /**
   * What sits beside the primary action, per stage — ordered by how likely it is to be
   * the NEXT press after the primary one.
   *   gathering: you take control, then start; the roster is what you are watching.
   *   teaching:  you advance, replay, or jump phase; marker and Direct are the attention pair.
   *   quiz:      you watch answers, and may replay or point at an option.
   *   review:    the roster IS the outcome.
   */
  const SURFACED: Record<typeof stage, string[]> = {
    // Before the class starts there is no student panel to toggle and nobody to
    // point anywhere, so studentUi and direct are not offered yet — they appear
    // in `teaching` below, once they can actually do something.
    gathering: ['roster'],
    teaching: ['next', 'scenes', 'phases', 'replay', 'direct', 'studentUi', 'roster'],
    quiz: ['roster', 'replay', 'direct', 'phases'],
    review: ['roster', 'scenes', 'replay', 'phases'],
  };

  const surfacedKeys = SURFACED[stage].filter((k) => k !== cta.key);
  /**
   * A control appears only once its precondition holds. Releasing control needs
   * control; the student panel and Direct view need a class that has started.
   * Offering them earlier gave a teacher buttons that silently did nothing.
   */
  const isAvailable = (key: string): boolean => {
    if (key === 'release') return controlStudentsEnabled;
    if (key === 'studentUi' || key === 'direct') return classStarted;
    // One phase is not a choice, and a single-stop tour is not a tour.
    if (key === 'phases') return phases.length > 1;
    if (key === 'scenes') return !!tourStops && tourStops.total > 1;
    return true;
  };
  const surfaced = (key: string) => surfacedKeys.includes(key) && isAvailable(key);

  // The marker travels with the teaching tools, and is hidden while gathering — there is
  // nothing to annotate before the lesson is on screen.
  // Only players that draw ink pass the marker; the XR player marks parts via Highlight instead.
  const showMarker = (stage === 'teaching' || stage === 'quiz') && !!onToggleMarker;

  // Shown whenever the scene holds a model at all. Gating on the TARGET's part
  // count would make the whole toolbar disappear the moment a teacher selected a
  // single-piece asset, taking the picker with it and leaving no way back.
  const showModel = isHost && (modelPartCount > 0 || modelAssets.length > 0);

  /*
    Layout — four zones that never scroll or clip:

      [primary] | [lesson flow: tour stops · phase stepper · next phase] | [tools] [3D model] [images] [direct] [replay]  …  [next topic ▾] [roster] [more]

    The bar used to put every control in one row with the phase buttons in a
    scroll strip whose scrollbar was hidden. Each new control (Next topic, Topics,
    the model tools) pushed Intro / Learn / Summary / Quiz further out of sight.
    Now the lesson flow has a fixed place, and the wide, occasional tools open as
    popovers above the bar instead of competing for its width.
  */
  const flowVisible = surfaced('phases');
  // Direct view is a primary teaching action, so it stays on the bar, never in a menu.
  const toolKeys = ['studentUi'].filter(surfaced);
  const hasTools = showMarker || toolKeys.length > 0;
  const inlineKeys = ['scenes', 'next', 'replay', 'roster', 'direct', 'studentUi', 'phases'];
  const moreKeys = Object.keys(controls).filter(
    (k) => k !== cta.key && isAvailable(k) && !(surfacedKeys.includes(k) && inlineKeys.includes(k))
  );

  const marker = (
    <MarkerToolbar
      active={markerActive}
      color={markerColor}
      compact={compact}
      onToggleActive={() => onToggleMarker?.()}
      onColorChange={(c) => onMarkerColorChange?.(c)}
    />
  );

  const model = (
    <ModelToolbar
      compact={false}
      assets={modelAssets}
      selectedAssetKey={modelSelectedAssetKey}
      onSelectAsset={(key) => onModelSelectAsset?.(key)}
      partCount={modelPartCount}
      explode={modelExplode}
      onExplodeChange={(t) => onModelExplodeChange?.(t)}
      isolated={modelIsolated}
      selectedPartName={modelSelectedPartName}
      onToggleIsolate={() => onToggleModelIsolate?.()}
      clip={modelClip}
      onClipChange={(c) => onModelClipChange?.(c)}
      onReset={() => onModelReset?.()}
      highlightMode={modelHighlightMode}
      onToggleHighlight={onToggleModelHighlight}
      highlightCount={modelHighlightCount}
      onClearHighlights={onClearModelHighlights}
    />
  );

  const divider = <div className="mx-0.5 hidden h-6 w-px shrink-0 bg-white/10 sm:block" />;

  // Phase stepper: the current phase filled, finished ones ticked, one press away each.
  const stepper = (
    <div className="flex shrink-0 items-center gap-0.5 rounded-lg border border-white/12 bg-white/[0.04] p-0.5" role="group" aria-label="Lesson phase">
      {phases.map((p, i) => {
        const current = phase === p.phase;
        const done = phaseIndex >= 0 && i < phaseIndex;
        return (
          <button
            key={p.phase}
            type="button"
            onClick={() => onPlaybackCommand?.('play', p.phase)}
            aria-current={current ? 'step' : undefined}
            title={`Send the class to ${p.label}`}
            className={`inline-flex h-8 items-center gap-1 rounded-md px-2.5 text-[11px] font-semibold transition ${
              current ? 'bg-white/15 text-white shadow-inner' : done ? 'text-emerald-200/80 hover:text-white' : 'text-white/60 hover:text-white'
            }`}
          >
            {done && <Check className="hidden h-3 w-3 lg:block" />}
            {p.label}
          </button>
        );
      })}
    </div>
  );

  // Compact: one pill naming where the class is; the list opens upward.
  const phasePill = (
    <button
      type="button"
      onClick={() => toggle('phases')}
      aria-expanded={menu === 'phases'}
      title="Choose the lesson phase"
      className={`${menuButton(menu === 'phases')} max-w-[8rem]`}
    >
      <span className="truncate">{phases[Math.max(phaseIndex, 0)]?.label ?? 'Phase'}</span>
      <span className="hidden tabular-nums text-white/50 min-[400px]:inline">{Math.max(phaseIndex, 0) + 1}/{phases.length}</span>
      <ChevronUp className="h-3.5 w-3.5 shrink-0" />
    </button>
  );

  // On a phone the topic list lives in More, so the bar keeps one topic button.
  const topicMenuInline = topics.length > 1 && !!onPickTopic && !compact;
  // The player is holding Next for the quiz (see `quizProgress`).
  const topicWaiting = !!nextTopicName && quizProgress?.open === false;
  const topicControl = isHost && chapterHasSequence && (
    <div className="flex shrink-0 items-stretch">
      {/*
        Move the class on to the next topic.

        Never disabled by the count, even while the player is holding it for
        the quiz. Waiting shows as amber "Waiting d/t", and a press still goes
        through: the player then asks the teacher to confirm and names who is
        still working, because a class sometimes has to leave a student whose
        headset died behind. Students carried forward keep the answers they
        gave, so moving on early costs nobody their work.
      */}
      <button
        type="button"
        onClick={() => onAdvanceTopic?.()}
        disabled={!nextTopicName || topicLaunchPending}
        aria-label={
          topicWaiting
            ? `Next topic: ${nextTopicName}. Waiting for the quiz: ${quizProgress?.done} of ${quizProgress?.total} finished`
            : nextTopicName
              ? `Next topic: ${nextTopicName}`
              : 'Last topic'
        }
        title={
          topicWaiting
            ? `${quizProgress?.done} of ${quizProgress?.total} students have finished the quiz. Moving on to "${nextTopicName}" now asks you to confirm.`
            : nextTopicName
              ? `Move the class on to "${nextTopicName}" without leaving immersive mode`
              : 'This is the last topic in the chapter'
        }
        className={`inline-flex h-10 items-center gap-1.5 border px-2.5 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-45 ${
          topicMenuInline ? 'rounded-l-lg' : 'rounded-lg'
        } ${
          topicWaiting
            ? 'border-amber-300/50 bg-amber-400/20 text-amber-50 hover:bg-amber-400/30'
            : nextTopicName
              ? 'border-emerald-300/50 bg-emerald-400/20 text-emerald-50 hover:bg-emerald-400/30'
              : 'border-white/12 bg-white/[0.06] text-white/70'
        }`}
      >
        {topicLaunchPending ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" /> : <SkipForward className="h-3.5 w-3.5 shrink-0" />}
        {!compact && (
          <span className="hidden max-w-[9rem] truncate xl:inline">
            {topicWaiting
              ? `Waiting ${quizProgress?.done}/${quizProgress?.total}`
              : nextTopicName
                ? `Next: ${nextTopicName}`
                : 'Last topic'}
          </span>
        )}
        {quizProgress && quizProgress.total > 0 && (
          <span
            className={`hidden rounded px-1 tabular-nums min-[400px]:inline ${
              // At xl the label already reads "Waiting d/t".
              topicWaiting && !compact ? 'xl:hidden' : ''
            } ${
              quizProgress.done >= quizProgress.total
                ? 'bg-emerald-300/30'
                : topicWaiting
                  ? 'bg-amber-300/30'
                  : 'bg-white/15 text-white/80'
            }`}
            title={`${quizProgress.done} of ${quizProgress.total} students have finished the quiz`}
          >
            {quizProgress.done}/{quizProgress.total}
          </span>
        )}
      </button>
      {topicMenuInline && (
        <button
          type="button"
          onClick={() => toggle('topics')}
          aria-expanded={menu === 'topics'}
          aria-label="Jump to any topic"
          title="Jump the class to any topic in this chapter"
          className={`inline-flex h-10 w-8 items-center justify-center rounded-r-lg border border-l-0 transition ${
            menu === 'topics'
              ? 'border-white/30 bg-white/15 text-white'
              : topicWaiting
                ? 'border-amber-300/50 bg-amber-400/10 text-amber-50 hover:bg-amber-400/25'
                : nextTopicName
                  ? 'border-emerald-300/50 bg-emerald-400/10 text-emerald-50 hover:bg-emerald-400/25'
                  : 'border-white/12 bg-white/[0.06] text-white/70 hover:bg-white/10'
          }`}
        >
          <ListOrdered className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );

  return (
    <div
      ref={barRef}
      className="pointer-events-auto relative flex h-full w-full items-center gap-1.5 border-t border-white/10 bg-zinc-950/80 px-2 backdrop-blur-2xl sm:gap-2 sm:px-3"
    >
      {/* Primary action for this stage */}
      <div className="shrink-0">{cta.node}</div>

      {/* Lesson flow — always on screen while there is a flow to steer */}
      {(flowVisible || surfaced('scenes')) && (
        <>
          {divider}
          <div className="flex shrink-0 items-center gap-1.5">
            {!compact && surfaced('scenes') && controls.scenes}
            {flowVisible && (compact ? phasePill : stepper)}
            {surfaced('next') && flowVisible && !compact && controls.next}
          </div>
        </>
      )}

      {/* Teaching tools — wide, occasional controls open above the bar */}
      {!compact && (hasTools || showModel || showImages || surfaced('replay') || surfaced('direct')) && (
        <>
          {divider}
          <div className="flex shrink-0 items-center gap-1.5">
            {showMarker && <div className="hidden shrink-0 2xl:flex">{marker}</div>}
            {hasTools && (
              <button
                type="button"
                onClick={() => toggle('tools')}
                aria-expanded={menu === 'tools'}
                title="Marker and the student panel"
                className={menuButton(menu === 'tools')}
              >
                <PenTool className="h-3.5 w-3.5" />
                <span className="hidden xl:inline">Tools</span>
                {markerActive && <span className="h-1.5 w-1.5 rounded-full" style={{ background: markerColor }} />}
              </button>
            )}
            {showModel && (
              <button
                type="button"
                onClick={() => toggle('model')}
                aria-expanded={menu === 'model'}
                title="Explode, isolate or section the 3D model"
                className={menuButton(menu === 'model')}
              >
                <Box className="h-3.5 w-3.5" />
                <span className="hidden xl:inline">3D model</span>
                {(modelExplode > 0 || modelIsolated || modelClip || modelHighlightCount > 0) && <span className="h-1.5 w-1.5 rounded-full bg-cyan-300" />}
              </button>
            )}
            {/* On a phone the grid is in More › Images. */}
            {showImages && imagesButton}
            {surfaced('direct') && controls.direct}
            {surfaced('replay') && controls.replay}
          </div>
        </>
      )}

      {/* On a phone Direct view still sits on the bar, as an icon */}
      {compact && surfaced('direct') && <div className="shrink-0">{controls.direct}</div>}

      <div className="min-w-0 flex-1" />

      {/* Topic navigation, the class, and the rest */}
      {topicControl}
      {surfaced('roster') && controls.roster}
      {(compact || moreKeys.length > 0) && (
        <button
          type="button"
          onClick={() => toggle('more')}
          aria-expanded={menu === 'more'}
          aria-label="More controls"
          title="More controls"
          className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border ${
            menu === 'more' ? 'border-white/30 bg-white/15 text-white' : 'border-white/12 bg-white/[0.06] text-white/75'
          }`}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      )}

      {/* ---- Popovers ---- */}
      {menu === 'phases' && (
        <div className={`${popover} left-2 flex flex-col gap-0.5`}>
          <div className={sectionLabel}>Lesson phase</div>
          {compact && surfaced('next') && (
            <button
              type="button"
              onClick={() => { goNextPhase(); setMenu(null); }}
              className="mb-1 flex h-9 items-center gap-2 rounded-md border border-teal-300/40 bg-teal-400/15 px-2 text-xs font-semibold text-teal-100"
            >
              <ChevronRight className="h-4 w-4" /> Next phase
            </button>
          )}
          {phases.map((p, i) => (
            <button
              key={p.phase}
              type="button"
              onClick={() => { onPlaybackCommand?.('play', p.phase); setMenu(null); }}
              className={`flex h-9 min-w-[10rem] items-center gap-2 rounded-md px-2 text-left text-xs font-semibold ${
                phase === p.phase ? 'bg-white/15 text-white' : 'text-white/70 hover:bg-white/10'
              }`}
            >
              <span className="w-4 tabular-nums text-white/40">{i + 1}</span>
              {p.label}
              {phaseIndex >= 0 && i < phaseIndex && <Check className="ml-auto h-3.5 w-3.5 text-emerald-300" />}
            </button>
          ))}
        </div>
      )}

      {menu === 'tools' && (
        <div className={`${popover} left-1/2 flex -translate-x-1/2 flex-col gap-2`}>
          {showMarker && (
            <div className="2xl:hidden">
              <div className={sectionLabel}>Annotate</div>
              {marker}
            </div>
          )}
          {toolKeys.length > 0 && (
            <div>
              <div className={sectionLabel}>Class view</div>
              <div className="flex flex-wrap items-center gap-1.5">{toolKeys.map((k) => controls[k])}</div>
            </div>
          )}
        </div>
      )}

      {menu === 'model' && (
        <div className={`${popover} left-1/2 -translate-x-1/2`}>
          <div className={sectionLabel}>3D model</div>
          <div className="overflow-x-auto">{model}</div>
        </div>
      )}

      {imagesPopover}

      {menu === 'topics' && onPickTopic && (
        <div className={`${popover} right-2`}>
          <TopicPicker
            topics={topics}
            disabled={topicLaunchPending}
            onPick={(t) => {
              setMenu(null);
              onPickTopic(t);
            }}
          />
        </div>
      )}

      {/* On a phone More holds tools, model, images and topics together, which
          can be taller than the screen: capped, it scrolls rather than pushing
          its top sections off the top edge. */}
      {menu === 'more' && (
        <div className={`${popover} right-2 flex max-h-[calc(100dvh-var(--hud-bottom)-1rem)] w-max flex-col gap-2 overflow-y-auto overscroll-contain`}>
          {compact && surfaced('scenes') && <div>{controls.scenes}</div>}
          {compact && (hasTools || surfaced('replay')) && (
            <div>
              <div className={sectionLabel}>Teaching tools</div>
              <div className="flex flex-wrap items-center gap-1.5">
                {showMarker && marker}
                {toolKeys.map((k) => controls[k])}
                {surfaced('replay') && controls.replay}
              </div>
            </div>
          )}
          {compact && showModel && (
            <div>
              <div className={sectionLabel}>3D model</div>
              <div className="overflow-x-auto">{model}</div>
            </div>
          )}
          {compact && showImages && (
            <div>
              <div className={sectionLabel}>Images</div>
              {imageGrid}
            </div>
          )}
          {compact && isHost && chapterHasSequence && topics.length > 1 && onPickTopic && (
            <div>
              <div className={sectionLabel}>Topics</div>
              <TopicPicker
                topics={topics}
                disabled={topicLaunchPending}
                onPick={(t) => {
                  setMenu(null);
                  onPickTopic(t);
                }}
              />
            </div>
          )}
          {moreKeys.length > 0 && (
            <div>
              <div className={sectionLabel}>Class</div>
              <div className="flex flex-wrap items-center gap-1.5">{moreKeys.map((k) => controls[k])}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
